import { defineStore } from 'pinia'
import {
  toUtf8,
  toBase64,
  WasmContractQueryResponse
} from '@injectivelabs/sdk-ts'
import { wasmApi } from '@shared/Service'
import {
  voteProject,
  addWhitelistedAddresses,
  removeWhitelistedAddresses
} from '@/store/vote/message'
import { VOTE_CONTRACT_ADDRESS } from '@/app/utils/constant'
import { VoteProject } from '@/types'

type VoteStoreState = {
  userVote: string
  isFinalized: boolean
  ownerAddress: string
  projects: VoteProject[]
  isUserWhitelisted: boolean
  whitelistedAddressesCount: number
}

const initialStateFactory = (): VoteStoreState => ({
  projects: [],
  userVote: '',
  ownerAddress: '',
  isFinalized: false,
  isUserWhitelisted: false,
  whitelistedAddressesCount: 0
})

export const useVoteStore = defineStore('vote', {
  state: (): VoteStoreState => initialStateFactory(),
  actions: {
    voteProject,
    addWhitelistedAddresses,
    removeWhitelistedAddresses,

    async fetchConfig() {
      const voteStore = useVoteStore()

      const response = (await wasmApi.fetchSmartContractState(
        VOTE_CONTRACT_ADDRESS,
        toBase64({ config: {} })
      )) as WasmContractQueryResponse

      const { is_finalized: isFinalized, owner } = JSON.parse(
        toUtf8(response.data)
      ) as {
        is_finalized: boolean
        owner: string
      }

      voteStore.$patch({
        isFinalized,
        ownerAddress: owner
      })
    },

    async fetchUserWhitelisted() {
      const voteStore = useVoteStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const response = (await wasmApi.fetchSmartContractState(
        VOTE_CONTRACT_ADDRESS,
        toBase64({
          user_whitelist: { user: sharedWalletStore.injectiveAddress }
        })
      )) as WasmContractQueryResponse

      const { is_whitelisted: isUserWhitelisted } = JSON.parse(
        toUtf8(response.data)
      ) as {
        is_whitelisted: boolean
      }

      voteStore.$patch({
        isUserWhitelisted
      })
    },

    async fetchWhitelistedAddressesCount() {
      const voteStore = useVoteStore()

      const response = (await wasmApi.fetchSmartContractState(
        VOTE_CONTRACT_ADDRESS,
        toBase64({ get_amount_whitelisted: {} })
      )) as WasmContractQueryResponse

      const count = Number(JSON.parse(toUtf8(response.data))) || 0

      voteStore.$patch({
        whitelistedAddressesCount: count
      })
    },

    async fetchProjects() {
      const voteStore = useVoteStore()

      const response = (await wasmApi.fetchSmartContractState(
        VOTE_CONTRACT_ADDRESS,
        toBase64({ vote_results: {} })
      )) as WasmContractQueryResponse

      voteStore.$patch({
        projects: JSON.parse(toUtf8(response.data)) as VoteProject[]
      })
    },

    async fetchUserVote() {
      const voteStore = useVoteStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const response = (await wasmApi.fetchSmartContractState(
        VOTE_CONTRACT_ADDRESS,
        toBase64({ user_vote: { user: sharedWalletStore.injectiveAddress } })
      )) as WasmContractQueryResponse

      const { vote } = JSON.parse(toUtf8(response.data)) as {
        vote: null | string
      }

      voteStore.$patch({
        userVote: vote || ''
      })
    },

    reset() {
      const voteStore = useVoteStore()
      const initialState = initialStateFactory()

      voteStore.$patch({
        ...initialState
      })
    }
  }
})
